import type {Dimensions} from '../types';
import type {ImageCodec, ImageCodecCapabilities} from './image-codec';
import {sniffImageFormat} from './sniff-image-format';

/** A 2×1 grey baseline JPEG carrying an Exif APP1 with orientation 6 (rotate 90° CW). */
const ORIENTATION_PROBE_JPEG_BASE64 =
  '/9j/4QAiRXhpZgAASUkqAAgAAAABABIBAwABAAAABgAAAAAAAAD/4AAQSkZJRgABAQAAAQABAAD/2wBDABALDA4MChAODQ4SERATGCgaGBYWGDEjJR0oOjM9PDkzODdASFxOQERXRTc4UG1RV19iZ2hnPk1xeXBkeFxlZ2P/wAALCAABAAIBAREA/8QAFAABAAAAAAAAAAAAAAAAAAAAAP/EABQQAQAAAAAAAAAAAAAAAAAAAAD/2gAIAQEAAD8AP//Z';

export const ORIENTATION_PROBE_STORED: Dimensions = {width: 2, height: 1};
export const ORIENTATION_PROBE_ORIENTED: Dimensions = {width: 1, height: 2};

export const orientationProbeBytes = (): Uint8Array<ArrayBuffer> => {
  const binary = atob(ORIENTATION_PROBE_JPEG_BASE64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
};

/**
 * Decodes the probe once and checks whether the engine rotated it. Engines that
 * accept `imageOrientation: 'from-image'` but ignore it are caught here, so the
 * pipeline only rotates manually when the decoder really did not.
 */
export const probeCodecCapabilities = async <TImage extends Dimensions>(
  codec: ImageCodec<TImage>,
): Promise<ImageCodecCapabilities> => {
  const bytes = orientationProbeBytes();
  const sniff = sniffImageFormat(bytes);
  if (sniff === null) {
    return {appliesExifOrientation: false};
  }
  try {
    const image = await codec.decode(new Blob([bytes], {type: sniff.mimeType}), sniff);
    const applies =
      image.width === ORIENTATION_PROBE_ORIENTED.width &&
      image.height === ORIENTATION_PROBE_ORIENTED.height;
    codec.close(image);
    return {appliesExifOrientation: applies};
  } catch {
    return {appliesExifOrientation: false};
  }
};
